import * as angular from 'angular';
import * as d3 from 'd3';

import { ComponentDefinition, TriggerAction, ITrigger, IAction, TriggerType, ActionType } from '../../../models';
import { TriggerOrAction, TriggerActionType } from '../TriggerOrAction';
import { SvgManager } from '../../../wtFramework';
import { TriggerActionSelectedItem } from '../TriggerActionSelectedItem';

const triggerIconsDic = {
    0: '\uf017', // clock, schedule
    1: '\uf06a', // exclamation, quality issue
    3: '\uf0f3', // bell, alarm
};

const actionIconsDic = {
    0: '\uf0ae', // tasks, reprocessing task
    3: '\uf003', // SendMail
};

const generalIconsDic = {
    play: '\uf144',
    pause: '\uf28c',
    configure: '\uf013'
};

// interface ITriggerAction {
//     triggerGuid: string;
//     actionGuid: string;
// };

const rectWidth = 200;
const rectHeight = 40;
const handleSize = 6;
const iconMargin = 15;
const iconWidth = 16;
const iconRightMargin = rectWidth - iconMargin;

class TriggerActionRelationalController implements angular.IComponentController {
    minHeight: number;

    readonly actions: IAction[];
    readonly isEditMode: boolean;
    readonly onConfigure: (arg: { item: TriggerActionSelectedItem }) => void;
    readonly onCreateLink: (arg: { triggerGuid: string; actionGuid: string }) => void;
    readonly onDelete: (arg: { item: TriggerActionSelectedItem; $event: { stopPropagation?: () => void } }) => void;
    readonly onSelectItem: (arg: { item: TriggerActionSelectedItem; $event: { stopPropagation?: () => void } }) => void;
    readonly onUpdate: (arg: { item: TriggerActionSelectedItem }) => void;
    readonly selectedItem: TriggerActionSelectedItem;
    readonly triggerActions: TriggerAction[];
    readonly triggers: ITrigger[];

    private _hoveredItem: TriggerOrAction;
    private _triggerScale: d3.scale.Ordinal<string, number>;
    private _actionScale: d3.scale.Ordinal<string, number>;
    private _width: number;

    static $inject = ['$element', '$translate'];

    constructor(
        private _$element: angular.IAugmentedJQuery,
        private _$translate: angular.translate.ITranslateService
    ) {
    }

    $onInit() {
        this.initScale();
        const margins = { marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 };
        const svgManager = new SvgManager('trigger-action-relational', margins);
        svgManager.addResizeFunction(this.onResize.bind(this));

        const container = d3.select(this._$element[0])
            .select('#trigger-action-relational')
            .on('keydown', d => {
                if (this.isEditMode) {
                    const event = d3.event as KeyboardEvent;
                    const { key, keyCode } = event;
                    if (key === 'Delete' || keyCode === 46) { // keyCode is obsolete, but I'm not sure if key is localized or not
                        if (this.selectItem) {
                            this.onDelete({ item: this.selectedItem, $event: event });
                        }
                    }
                }
            });
        const clipPath = container
            .select('svg')
            .append('defs')
            .append('clipPath')
            .attr('id', 'node-clip-path');
        clipPath.append('rect')
            .attr('x', iconWidth + iconMargin)
            .attr('width', rectWidth - 2 * iconMargin - 2 * iconWidth)
            .attr('height', rectHeight);
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        const container = d3.select(this._$element[0])
            .select('svg')
            .select('g.global');

        if (changesObj['isEditMode']) {
            container.classed('editable', this.isEditMode);
        }
        this.initScale();
        if (this.triggers) {
            this._triggerScale.domain(this.triggers.map(t => t.guid));
            this.renderTriggers(container.select('g.triggers'), this.triggers);
        }
        if (this.actions) {
            const actionsContainer = container.select('g.actions');
            this._actionScale.domain(this.actions.map(t => t.guid));
            this.renderActions(actionsContainer, this.actions);
        }
        if (this.triggerActions) {
            this.renderLinks(container.select('g.links'), this.triggerActions);
        }

        const max = d3.max([this.triggers?.length, this.actions?.length]);
        // compte min height has the number of rect + 10% for margins
        this.minHeight = max * rectHeight * 1.1;
    }

    selectItem(item: TriggerOrAction, type: TriggerActionType, $event: angular.IAngularEvent) {
        const selectedItem = this.getItem(item, type);
        this.onSelectItem({ item: selectedItem, $event });
    }

    private getItem(item: TriggerOrAction, type: TriggerActionType): TriggerActionSelectedItem {
        return type !== null && type !== undefined
            ? {
                type: type,
                trigger: type === TriggerActionType.Trigger ? item as ITrigger : null,
                action: type === TriggerActionType.Action ? item as IAction : null,
                link: type === TriggerActionType.TriggerActionLink ? item as TriggerAction : null,
            }
            : null;
    }

    private updateStartStop(item: TriggerOrAction, type: TriggerActionType) {
        (item as any).isDisabled = !(item as any).isDisabled;
        this.onUpdate({ item: this.getItem(item, type) });
    }

    private openConfigure(item: TriggerOrAction, type: TriggerActionType) {
        this.onConfigure({ item: this.getItem(item, type) });
    }

    private initScale() {
        this._triggerScale = this._triggerScale || d3.scale.ordinal<string, number>();
        this._actionScale = this._actionScale || d3.scale.ordinal<string, number>();
    }

    private onResize(width: number, height: number) {
        this._triggerScale.rangeBands([0, height], .1);
        this._actionScale.rangeBands([0, height], .1);
        this._width = width;
        const global = d3.select(this._$element[0])
            .select('svg')
            .select('g.global');
        global.select('g.actions')
            .attr('transform', `translate(${width - rectWidth - 2},0)`);
        global.select('g.links')
            .attr('transform', `translate(${rectWidth}, 0)`);
        this.render();
    }

    private render() {
        const container = d3.select(this._$element[0])
            .select('svg')
            .select('g.global');
        if (this.triggers) {
            this.renderTriggers(container.select('g.triggers'), this.triggers);
        }
        if (this.actions) {
            const actionsContainer = container.select('g.actions');
            this.renderActions(actionsContainer, this.actions);
        }
        if (this.triggerActions) {
            this.renderLinks(container.select('g.links'), this.triggerActions);
        }
    }

    private renderLinks(container: d3.Selection<any>, triggerActions: TriggerAction[]) {
        const diagonal = d3.svg.diagonal();
        const mapped = triggerActions.map(d => {
            return {
                source: { x: 0, y: this._triggerScale(d.triggerGuid) + this._triggerScale.rangeBand() / 2 },
                target: { x: this._width - 2 * rectWidth - 2, y: this._actionScale(d.actionGuid) + this._actionScale.rangeBand() / 2 },
                item: d
            };
        });
        const bound = container.selectAll('g.link')
            .data(mapped);
        bound
            .exit()
            .remove();
        const enterSelection = bound
            .enter()
            .append('g')
            .classed('link', true)
            .on('click', d => {
                if (this.onSelectItem) {
                    this.onSelectItem({
                        item: {
                            link: d.item,
                            type: TriggerActionType.TriggerActionLink
                        },
                        $event: <any>d3.event
                    });
                }
            });
        enterSelection.append('path');
        enterSelection.append('title');
        bound
            .style('pointer-events', this.isEditMode ? 'all' : 'none')
            .classed('selected', d => this.isEditMode && this.selectedItem && d.item === this.selectedItem.link)
            .select('path')
            .attr('d', diagonal);
        bound.select('title')
            .text(this._$translate.instant('Press Delete key to remove the link'));
    }

    private renderActions(container: d3.Selection<any>, actions: IAction[]) {
        const bound = container
            .selectAll('g.action')
            .data(actions);
        bound.exit()
            .remove();
        const enterGroup = this
            .enter(bound, TriggerActionType.Action);

        this.appendHandle(enterGroup)
            .attr('transform', `translate(${- handleSize / 2}, ${rectHeight / 2 - handleSize / 2})`);
        this.updateActions(bound);
        bound.select('.handle')
            .classed('hidden', d => !this.isEditMode || !d.guid);
    }

    private renderTriggers(container: d3.Selection<any>, triggers: ITrigger[]) {
        const bound = container
            .selectAll('g.trigger')
            .data(triggers);
        bound.exit()
            .remove();
        const enterGroup = this.enter(bound, TriggerActionType.Trigger);
        this.appendHandle(enterGroup)
            .attr('transform', `translate(${rectWidth - handleSize / 2}, ${rectHeight / 2 - handleSize / 2})`);
        this.updateTriggers(bound);
        bound.select('.handle')
            .classed('hidden', d => !this.isEditMode || !d.guid);
    }

    private appendHandle<T>(container: d3.Selection<T>) {
        const group = container
            .append('g')
            .classed('handle', true)
            .append('circle')
            .attr('r', handleSize / 2)
            .attr('cx', handleSize / 2)
            .attr('cy', handleSize / 2);
        return group;
    }

    private enter(bound: d3.selection.Update<TriggerOrAction>, type: TriggerActionType) {
        const self = this;
        const enterGroup = bound.enter()
            .append('g')
            .classed(TriggerActionType[type].toLowerCase(), true)
            .style('pointer-events', 'all')
            .on('click', d => {
                if (this.isEditMode) {
                    this.selectItem(d, type, d3.event as any);
                }
            })
            .on('mouseenter', function (d, i) {
                self._hoveredItem = d;
            })
            .on('mouseout', function (d, i) {
                self._hoveredItem = null;
            });
        enterGroup.append('title');
        enterGroup.append('rect')
            .attr('width', rectWidth)
            .attr('height', rectHeight)
            .attr('rx', 5);
        enterGroup.append('text')
            .attr('transform', `translate(${iconMargin}, 25)`)
            .classed('fa-icon', true)
            .classed('type-icon', true);
        enterGroup.append('text')
            .attr('transform', `translate(${iconRightMargin}, 25)`)
            .classed('fa-icon', true)
            .classed('play-icon', d => (d as any).isDisabled)
            .classed('pause-icon', d => !(d as any).isDisabled)
            .on('click', d => this.updateStartStop(d, type));
        enterGroup.append('text')
            .attr('transform', `translate(${iconRightMargin}, 25)`)
            .classed('fa-icon configure-icon', true)
            .style('text-anchor', 'end')
            .attr('x', rectWidth)
            .attr('transform', `translate(-${iconMargin*2}, 25)`)
            .on('click', d => this.openConfigure(d, type));
        enterGroup.append('text')
            .classed('name', true)
            .attr('x', rectWidth / 2)
            .attr('y', 25)
            .style('pointer-events', 'none')
            .style('clip-path', `url("#node-clip-path")`);
        enterGroup.append('text')
            .classed('fa-icon', true)
            .classed('delete-icon', true)
            .style('text-anchor', 'end')
            .attr('x', rectWidth)
            .attr('transform', `translate(-${iconMargin}, 25)`)
            .text('\uf014')//delete icon
            .on('click', d => {
                const selectedItem = {
                    type: type,
                    trigger: type === TriggerActionType.Trigger ? d as ITrigger : null,
                    action: type === TriggerActionType.Action ? d as IAction : null,
                    link: type === TriggerActionType.TriggerActionLink ? d as TriggerAction : null,
                };
                this.onDelete({ item: selectedItem, $event: d3.event as any });
            })
            .append('title');
        return enterGroup;
    }

    private updateTriggers(bound: d3.selection.Update<ITrigger>) {
        if (this._triggerScale) {
            this.setDragBehavior(bound);
            const offset = this._triggerScale.rangeBand() / 2 - rectHeight / 2;
            this.updateButtons(bound, TriggerActionType.Trigger);
            bound.classed('schedule-trigger', d => d.type === TriggerType.Schedule)
                .classed('selected', d => this.isEditMode && this.selectedItem && d === this.selectedItem.trigger)
                .attr('transform', d => `translate(0, ${this._triggerScale(d.guid) + offset})`)
                .select('title')
                .text(d => d.name);
            bound.select('text.type-icon')
                .text(d => triggerIconsDic[d.type]);
            bound.select('text.name')
                .classed('disabled', d => (d as any).isDisabled)
                .text(d => d.name);
            bound.select('text.delete-icon')
                .classed('hidden', d => !this.isEditMode || !d.guid || this.triggerActions.filter(t => t.triggerGuid === d.guid).length > 0)
                .select('title')
                .text(this._$translate.instant('Delete'));
        }
    }

    private updateActions(bound: d3.selection.Update<IAction>) {
        if (this._actionScale) {
            this.setActionDragBehavior(bound);
            const offset = this._actionScale.rangeBand() / 2 - rectHeight / 2;
            this.updateButtons(bound, TriggerActionType.Action);
            bound.classed('reprocessing-action', d => d.type === ActionType.ReprocessingTask)
                .classed('selected', d => this.selectedItem && d === this.selectedItem.action)
                .attr('transform', d => `translate(0, ${this._actionScale(d.guid) + offset})`)
                .select('title')
                .text(d => d.name);
            bound.select('text.type-icon')
                .text(d => actionIconsDic[d.type]);
            bound.select('text.name')
                .classed('disabled', d => (d as any).isDisabled)
                .text(d => d.name);
            bound.select('text.delete-icon')
                .classed('hidden', d => !this.isEditMode || !d.guid || this.triggerActions.filter(t => t.actionGuid === d.guid).length > 0);
        }
    }

    private updateButtons(bound: d3.selection.Update<TriggerOrAction>, type: TriggerActionType) {
        bound.select('text.play-icon,text.pause-icon')
            .classed('editable', true)
            .classed('hidden', d => this.isEditMode)
            .classed('play-icon', d => (d as any).isDisabled)
            .classed('pause-icon', d => !(d as any).isDisabled);
        bound.select('text.play-icon')
            .text(d => generalIconsDic.play)
            .append('title')
            .text(this._$translate.instant('Start'));
        bound.select('text.pause-icon')
            .text(d => generalIconsDic.pause)
            .append('title')
            .text(this._$translate.instant('Stop'));
        bound.select('text.configure-icon')
            .classed('hidden', d => !this.isEditMode || !this.hasConfigure(d, type))
            .text(d => generalIconsDic.configure)
            .append('title')
            .text(this._$translate.instant('Settings'));
    }

    private hasConfigure(item: TriggerOrAction, type: TriggerActionType): boolean {
        return (type === TriggerActionType.Action && (item as IAction).type === ActionType.SendMail);
    }

    private setDragBehavior(bound: d3.Selection<ITrigger>) {
        let trigger: ITrigger;
        let line: d3.Selection<any>;
        const self = this;
        const dragBehavior = d3.behavior.drag<ITrigger>()
            .on('dragstart', function (d, i) {
                trigger = d;
                line = d3.select(self._$element[0])
                    .select('g.links')
                    .append('line')
                    .classed('tmp', true)
                    .attr('x1', 0)
                    .attr('y1', self._triggerScale(d.guid) + self._triggerScale.rangeBand() / 2)
                    .attr('x2', 0)
                    .attr('y2', self._triggerScale(d.guid) + self._triggerScale.rangeBand() / 2);
            })
            .on('drag', function (d, i) {
                let x2 = +line.attr('x2');
                x2 += (d3.event as any).dx;
                let y2 = +line.attr('y2');
                y2 += (d3.event as any).dy;
                line
                    .attr('x2', x2)
                    .attr('y2', y2);
            })
            .on('dragend', function (d, i) {
                if (self._hoveredItem && self.actions.indexOf(self._hoveredItem as any) >= 0) {
                    const datum = self._hoveredItem as IAction;
                    if (self.onCreateLink && datum) {
                        self.onCreateLink({ triggerGuid: trigger.guid, actionGuid: datum.guid });
                    }
                }
                line.remove();
            });
        bound.select('g.handle')
            .select('circle')
            .call(dragBehavior);
    }


    private setActionDragBehavior(bound: d3.Selection<IAction>) {
        let action: IAction;
        let line: d3.Selection<any>;
        const self = this;
        const dragBehavior = d3.behavior.drag<IAction>()
            .on('dragstart', function (d, i) {
                action = d;
                line = d3.select(self._$element[0])
                    .select('g.links')
                    .append('line')
                    .classed('tmp', true)
                    .attr('x1', self._width - 2 * rectWidth - 2)
                    .attr('y1', self._actionScale(d.guid) + self._actionScale.rangeBand() / 2)
                    .attr('x2', self._width - 2 * rectWidth - 2)
                    .attr('y2', self._actionScale(d.guid) + self._actionScale.rangeBand() / 2);
            })
            .on('drag', function (d, i) {
                let x2 = +line.attr('x2');
                x2 += (d3.event as any).dx;
                let y2 = +line.attr('y2');
                y2 += (d3.event as any).dy;
                line
                    .attr('x2', x2)
                    .attr('y2', y2);
            })
            .on('dragend', function (d, i) {
                if (self._hoveredItem && self.triggers.indexOf(self._hoveredItem as any) >= 0) {
                    const datum = self._hoveredItem as ITrigger;
                    if (self.onCreateLink && datum) {
                        self.onCreateLink({ triggerGuid: datum.guid, actionGuid: action.guid });
                    }
                }
                line.remove();
            });
        bound.select('g.handle')
            .select('circle')
            .call(dragBehavior);
    }
}

export const wtTriggerActionRelational: ComponentDefinition = {
    componentId: 'wtTriggerActionRelational',
    controller: TriggerActionRelationalController,
    template: require('./triggerActionRelational.component.html'),
    controllerAs: 'vm',
    bindings: {
        triggers: '<',
        actions: '<',
        triggerActions: '<',
        onConfigure: '&?',
        onCreateLink: '&?',
        onDelete: '&?',
        onDrop: '&?',
        onSelectItem: '&?',
        onUpdate: '&',
        selectedItem: '<',
        isEditMode: '<',
        isLoading: '<?'
    }
};